import React, { useEffect, useState, createContext, useContext } from 'react';
import { useOrders, OrderItem, PaymentMethod } from './OrdersContext';
import { useBranches, isKdsEnabled } from './BranchesContext';
export type TabItem = OrderItem & {
  sentToKitchenAt?: number;
  deliveredAt?: number;
  deliveredBy?: string;
  // True when this item is a complimentary remake of a previously delivered
  // item (quality issue). Comp items don't contribute to the tab subtotal,
  // but the original price is preserved on the line for waste/loss reporting.
  complimentary?: boolean;
  remakeReason?: string;
  // When this item is a remake or repeat of another, points back to the
  // original delivered item — keeps the audit trail intact.
  originalItemId?: string;
};
export type Tab = {
  id: string;
  tableNumber: string;
  guestCount: number;
  waiterPin: string;
  status: 'open' | 'closed';
  items: TabItem[];
  openedAt: number;
  orderId?: string;
  orderType: 'dine-in' | 'takeout';
  packagingId?: string;
  customerName?: string;
  // Branch this tab belongs to. Optional for legacy seed tabs; readers
  // default to 'b_main' when missing.
  branchId?: string;
};
type TabsContextType = {
  tabs: Tab[];
  openTab: (tableNumber: string, guestCount: number, waiterPin: string, orderType?: 'dine-in' | 'takeout', customerName?: string) => string;
  addItemToTab: (tabId: string, item: Omit<TabItem, 'id'>) => void;
  updateTabItem: (tabId: string, itemId: string, patch: Partial<TabItem>) => void;
  removeTabItem: (tabId: string, itemId: string) => void;
  updateTab: (tabId: string, patch: Partial<Tab>) => void;
  sendToKitchen: (tabId: string, packagingFee?: number) => void;
  closeTab: (tabId: string, splitCount?: number, itemAssignments?: Record<string, number>, paymentMethod?: PaymentMethod) => void;
  markDelivered: (tabId: string, itemId: string, waiterPin: string) => void;
  // DEPRECATED: prefer remakeItem (no charge) or reorderItem (charged).
  refireItem: (tabId: string, itemId: string) => void;
  // Customer rejected the dish (too cold, wrong cook, dropped). Creates a
  // NEW pending tab item that's a comp (no charge), preserving the original
  // delivered audit trail. Kitchen ticket will mark it as a REMAKE.
  remakeItem: (tabId: string, itemId: string, reason?: string) => void;
  // Customer wants another of the same dish. Creates a NEW pending tab item,
  // charged normally. Kitchen ticket is standard.
  reorderItem: (tabId: string, itemId: string) => void;
};
const TabsContext = createContext<TabsContextType | null>(null);
const initialTabs: Tab[] = [{
  id: 't1',
  tableNumber: '4',
  guestCount: 2,
  waiterPin: '1234',
  status: 'open',
  orderType: 'dine-in',
  openedAt: Date.now() - 1000 * 60 * 15,
  items: [{
    id: 'i1',
    name: 'Shiro Wot',
    quantity: 1,
    unitPrice: 150,
    sentToKitchenAt: Date.now() - 1000 * 60 * 14,
    deliveredAt: Date.now() - 1000 * 60 * 8,
    deliveredBy: '1234'
  }, {
    id: 'i2',
    name: 'Coca Cola',
    quantity: 2,
    unitPrice: 40,
    sentToKitchenAt: Date.now() - 1000 * 60 * 14,
    deliveredAt: Date.now() - 1000 * 60 * 10,
    deliveredBy: '1234'
  }]
}, {
  id: 't2',
  tableNumber: '12',
  guestCount: 4,
  waiterPin: '1234',
  status: 'open',
  orderType: 'dine-in',
  openedAt: Date.now() - 1000 * 60 * 45,
  items: [{
    id: 'i3',
    name: 'Doro Wot',
    quantity: 2,
    unitPrice: 300,
    sentToKitchenAt: Date.now() - 1000 * 60 * 40
  }, {
    id: 'i4',
    name: 'Tej',
    quantity: 4,
    unitPrice: 80,
    sentToKitchenAt: Date.now() - 1000 * 60 * 40
  }, {
    id: 'i5',
    name: 'Coffee',
    quantity: 2,
    unitPrice: 30
    // Not sent yet
  }]
}];
export function TabsProvider({
  children
}: {
  children: ReactNode;
}) {
  const {
    orders,
    addOrder,
    updateOrder
  } = useOrders();
  const {
    branches,
    branchId
  } = useBranches();
  const [tabs, setTabs] = useState<Tab[]>(initialTabs);
  // Mirror per-item `ready` / `readyAt` from the canonical order back into the
  // tab's local items array, so the waiter terminal's "Ready for pickup"
  // section actually reacts when the kitchen flips an item ready.
  useEffect(() => {
    setTabs(prev => prev.map(tab => {
      if (!tab.orderId) return tab;
      const order = orders.find(o => o.id === tab.orderId);
      if (!order) return tab;
      let changed = false;
      const nextItems = tab.items.map(it => {
        const oi = order.items.find(x => x.id === it.id);
        if (!oi) return it;
        if (oi.ready === it.ready && oi.readyAt === it.readyAt) return it;
        changed = true;
        return {
          ...it,
          ready: oi.ready,
          readyAt: oi.readyAt
        };
      });
      return changed ? {
        ...tab,
        items: nextItems
      } : tab;
    }));
  }, [orders]);
  const openTab = (tableNumber: string, guestCount: number, waiterPin: string, orderType: 'dine-in' | 'takeout' = 'dine-in', customerName?: string) => {
    const id = `tab_${Date.now()}`;
    const newTab: Tab = {
      id,
      tableNumber,
      guestCount,
      waiterPin,
      status: 'open',
      orderType,
      customerName,
      items: [],
      openedAt: Date.now()
    };
    setTabs(prev => [...prev, newTab]);
    return id;
  };
  const addItemToTab = (tabId: string, item: Omit<TabItem, 'id'>) => {
    setTabs(prev => prev.map(tab => {
      if (tab.id !== tabId) return tab;
      // Guard: Cannot add items to a closed tab
      if (tab.status === 'closed') {
        console.warn('Attempted to add item to a closed tab');
        return tab;
      }
      // Check if an identical item (same name, variation, modifiers, notes) already exists
      // and hasn't been sent to the kitchen yet. If so, just bump the quantity.
      const existingUnsentIdx = tab.items.findIndex(i => !i.sentToKitchenAt && i.name === item.name && i.variation === item.variation);
      if (existingUnsentIdx >= 0) {
        const newItems = [...tab.items];
        newItems[existingUnsentIdx] = {
          ...newItems[existingUnsentIdx],
          quantity: newItems[existingUnsentIdx].quantity + item.quantity
        };
        return {
          ...tab,
          items: newItems
        };
      }
      return {
        ...tab,
        items: [...tab.items, {
          ...item,
          id: `ti_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        }]
      };
    }));
  };
  const updateTabItem = (tabId: string, itemId: string, patch: Partial<TabItem>) => {
    setTabs(prev => prev.map(tab => {
      if (tab.id !== tabId) return tab;
      return {
        ...tab,
        items: tab.items.map(item => item.id === itemId ? {
          ...item,
          ...patch
        } : item)
      };
    }));
  };
  // Remove an item from a tab. Only safe for items that have NOT been sent
  // to the kitchen yet. Items already in flight should be cancelled/refired
  // through the existing flows so the kitchen isn't out of sync.
  const removeTabItem = (tabId: string, itemId: string) => {
    setTabs(prev => prev.map(tab => {
      if (tab.id !== tabId) return tab;
      const target = tab.items.find(it => it.id === itemId);
      if (!target) return tab;
      if (target.sentToKitchenAt) {
        console.warn('removeTabItem: refusing to remove an item already sent to the kitchen. Use cancellation flow instead.');
        return tab;
      }
      return {
        ...tab,
        items: tab.items.filter(it => it.id !== itemId)
      };
    }));
  };
  const updateTab = (tabId: string, patch: Partial<Tab>) => {
    setTabs(prev => prev.map(tab => tab.id === tabId ? {
      ...tab,
      ...patch
    } : tab));
  };
  const sendToKitchen = (tabId: string, packagingFee: number = 0) => {
    const activeBranch = branches.find(b => b.id === branchId) || branches[0];
    const kdsEnabled = isKdsEnabled(activeBranch);
    const now = Date.now();
    setTabs(prev => prev.map(tab => {
      if (tab.id !== tabId) return tab;
      const items = tab.items.map(it => ({
        ...it,
        sentToKitchenAt: it.sentToKitchenAt || now,
        ready: kdsEnabled ? it.ready : true,
        readyAt: kdsEnabled ? it.readyAt : it.readyAt || now
      }));
      // If this is the first time sending items, create the order
      if (!tab.orderId) {
        const newOrderId = `ORD-${Math.floor(Math.random() * 10000)}`;
        const subtotal = items.reduce((sum, it) => {
          if (it.complimentary) return sum;
          const mods = it.modifiers?.reduce((s, m) => s + m.price, 0) || 0;
          return sum + (it.unitPrice + mods) * it.quantity;
        }, 0);
        addOrder({
          id: newOrderId,
          customerName: tab.customerName || `Table ${tab.tableNumber}`,
          customerPhone: '',
          method: tab.orderType === 'dine-in' ? 'table' : 'takeout',
          tableNumber: tab.tableNumber,
          status: kdsEnabled ? 'preparing' : 'ready',
          paymentStatus: 'pending',
          // paymentMethod left undefined — waiter selects it when closing
          // the tab. Don't lie about cash before payment has happened.
          items,
          subtotal,
          packagingFee,
          serviceFee: subtotal * 0.05,
          total: subtotal + packagingFee + subtotal * 0.05,
          placedAt: now,
          estimatedMinutes: 15,
          tabId: tab.id,
          isOpenTab: true
        });
        return {
          ...tab,
          orderId: newOrderId,
          items,
          packagingFee
        };
      } else {
        // Update existing order
        const subtotal = items.reduce((sum, it) => {
          if (it.complimentary) return sum;
          const mods = it.modifiers?.reduce((s, m) => s + m.price, 0) || 0;
          return sum + (it.unitPrice + mods) * it.quantity;
        }, 0);
        updateOrder(tab.orderId, {
          items,
          subtotal,
          packagingFee,
          serviceFee: subtotal * 0.05,
          total: subtotal + packagingFee + subtotal * 0.05,
          status: kdsEnabled ? 'preparing' : 'ready'
        });
        return {
          ...tab,
          items,
          packagingFee
        };
      }
    }));
  };
  const closeTab = (tabId: string, splitCount: number = 1, itemAssignments?: Record<string, number>, paymentMethod?: PaymentMethod) => {
    setTabs(prev => prev.map(tab => {
      if (tab.id === tabId) {
        if (tab.orderId) {
          const order = orders.find(o => o.id === tab.orderId);
          if (order) {
            if (splitCount > 1 && itemAssignments) {
              // BY-ITEM split: each item goes to its assigned guest;
              // unassigned items are split equally across all guests.
              const lineTotal = (it: (typeof order.items)[number]) => {
                const modsTotal = it.modifiers?.reduce((s, m) => s + m.price, 0) || 0;
                return (it.unitPrice + modsTotal) * it.quantity;
              };
              const splitPackaging = +(order.packagingFee / splitCount).toFixed(2);
              // Build per-guest item lists + shared-cost-per-guest
              const sharedItems = order.items.filter(it => itemAssignments[it.id] === undefined);
              const sharedTotal = sharedItems.reduce((s, it) => s + lineTotal(it), 0);
              const sharedPerGuest = +(sharedTotal / splitCount).toFixed(2);
              for (let g = 0; g < splitCount; g++) {
                const assigned = order.items.filter(it => itemAssignments[it.id] === g);
                const assignedSubtotal = assigned.reduce((s, it) => s + lineTotal(it), 0);
                const subtotal = +(assignedSubtotal + sharedPerGuest).toFixed(2);
                const serviceFee = +(subtotal * 0.05).toFixed(2);
                const total = +(subtotal + serviceFee + splitPackaging).toFixed(2);
                const items = [...assigned, ...(sharedPerGuest > 0 ? [{
                  id: `shared_${g}`,
                  name: 'Shared portion',
                  quantity: 1,
                  unitPrice: sharedPerGuest
                }] : [])];
                // If the waiter recorded payment at the table, mark it as
                // paid with the method they collected. Otherwise leave the
                // order pending for the cashier to settle.
                const paymentPatch = paymentMethod ? {
                  paymentMethod,
                  paymentStatus: 'paid' as const
                } : {};
                if (g === 0) {
                  updateOrder(tab.orderId, {
                    isOpenTab: false,
                    status: paymentMethod ? 'completed' : 'ready',
                    customerName: `${order.customerName} (Split 1/${splitCount})`,
                    items,
                    subtotal,
                    packagingFee: splitPackaging,
                    serviceFee,
                    total,
                    ...paymentPatch
                  });
                } else {
                  addOrder({
                    ...order,
                    id: `${order.id}-S${g + 1}`,
                    isOpenTab: false,
                    status: paymentMethod ? 'completed' : 'ready',
                    customerName: `${order.customerName} (Split ${g + 1}/${splitCount})`,
                    items,
                    subtotal,
                    packagingFee: splitPackaging,
                    serviceFee,
                    total,
                    paymentStatus: paymentMethod ? 'paid' : 'pending',
                    ...(paymentMethod ? {
                      paymentMethod
                    } : {})
                  });
                }
              }
            } else if (splitCount > 1) {
              // EVEN split: divide totals and create additional orders
              const splitSubtotal = +(order.subtotal / splitCount).toFixed(2);
              const splitPackaging = +(order.packagingFee / splitCount).toFixed(2);
              const splitService = +(order.serviceFee / splitCount).toFixed(2);
              const splitTotal = +(order.total / splitCount).toFixed(2);
              const paymentPatch = paymentMethod ? {
                paymentMethod,
                paymentStatus: 'paid' as const
              } : {};
              updateOrder(tab.orderId, {
                isOpenTab: false,
                status: paymentMethod ? 'completed' : 'ready',
                customerName: `${order.customerName} (Split 1/${splitCount})`,
                subtotal: splitSubtotal,
                packagingFee: splitPackaging,
                serviceFee: splitService,
                total: splitTotal,
                ...paymentPatch
              });
              for (let i = 2; i <= splitCount; i++) {
                addOrder({
                  ...order,
                  id: `${order.id}-S${i}`,
                  isOpenTab: false,
                  status: paymentMethod ? 'completed' : 'ready',
                  customerName: `${order.customerName} (Split ${i}/${splitCount})`,
                  subtotal: splitSubtotal,
                  packagingFee: splitPackaging,
                  serviceFee: splitService,
                  total: splitTotal,
                  paymentStatus: paymentMethod ? 'paid' : 'pending',
                  ...(paymentMethod ? {
                    paymentMethod
                  } : {})
                });
              }
            } else {
              // Standard close — waiter has delivered, so the order is done.
              // If they also collected payment at the table, mark it paid;
              // otherwise leave for the cashier.
              updateOrder(tab.orderId, {
                isOpenTab: false,
                status: paymentMethod ? 'completed' : 'ready',
                ...(paymentMethod ? {
                  paymentMethod,
                  paymentStatus: 'paid' as const
                } : {})
              });
            }
          }
        }
        return {
          ...tab,
          status: 'closed'
        };
      }
      return tab;
    }));
  };
  const markDelivered = (tabId: string, itemId: string, waiterPin: string) => {
    const now = Date.now();
    setTabs(prev => prev.map(tab => {
      if (tab.id !== tabId) return tab;
      const items = tab.items.map(it => it.id === itemId ? {
        ...it,
        deliveredAt: now,
        deliveredBy: waiterPin
      } : it);
      if (tab.orderId) {
        // Surgically patch only the matching order item so we don't
        // clobber kitchen-managed fields like ready/readyAt.
        const order = orders.find(o => o.id === tab.orderId);
        if (order) {
          updateOrder(tab.orderId, {
            items: order.items.map(oi => oi.id === itemId ? {
              ...oi,
              deliveredAt: now,
              deliveredBy: waiterPin
            } : oi)
          });
        }
      }
      return {
        ...tab,
        items
      };
    }));
  };
  // Internal helper. Duplicates the original delivered item into a new
  // pending tab line (not yet sent to the kitchen). The original line is
  // left untouched to preserve audit history.
  const duplicateItemAsPending = (tabId: string, itemId: string, overrides: Partial<TabItem>) => {
    setTabs(prev => prev.map(tab => {
      if (tab.id !== tabId) return tab;
      const original = tab.items.find(it => it.id === itemId);
      if (!original) return tab;
      const duplicate: TabItem = {
        ...original,
        id: `ti_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        sentToKitchenAt: undefined,
        deliveredAt: undefined,
        deliveredBy: undefined,
        ready: undefined,
        readyAt: undefined,
        originalItemId: original.id,
        ...overrides
      };
      return {
        ...tab,
        items: [...tab.items, duplicate]
      };
    }));
  };
  const remakeItem = (tabId: string, itemId: string, reason?: string) => {
    duplicateItemAsPending(tabId, itemId, {
      complimentary: true,
      remakeReason: reason || ''
    });
  };
  const reorderItem = (tabId: string, itemId: string) => {
    duplicateItemAsPending(tabId, itemId, {
      complimentary: false
    });
  };
  // Legacy: refireItem used to reset the original line in place, which
  // erased delivery history. Treat it as a remake without a reason for
  // backwards compat with any older callers.
  const refireItem = (tabId: string, itemId: string) => {
    remakeItem(tabId, itemId);
  };
  return <TabsContext.Provider value={{
    tabs,
    openTab,
    addItemToTab,
    updateTabItem,
    removeTabItem,
    updateTab,
    sendToKitchen,
    closeTab,
    markDelivered,
    refireItem,
    remakeItem,
    reorderItem
  }} data-id="element-3583">
      {children}
    </TabsContext.Provider>;
}
export function useTabs() {
  const context = useContext(TabsContext);
  if (!context) throw new Error('useTabs must be used within TabsProvider');
  return context;
}